import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { parseClipboardData, getDetectionBadge, type ParsedData, type DetectionBadge } from './spectraUtils';

interface SpectraDataGridProps {
  data: ParsedData | null;
  onDataChange: (data: ParsedData) => void;
}

const SpectraDataGrid: React.FC<SpectraDataGridProps> = ({ data, onDataChange }) => {
  const gridRef = useRef<AgGridReact>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle paste events on the container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain');
      if (!text) return;

      // If grid is already populated and a cell is being edited, let AG Grid handle it
      if (data && document.activeElement?.closest('.ag-cell-editing')) return;

      e.preventDefault();
      const parsed = parseClipboardData(text);
      onDataChange(parsed);
    };

    el.addEventListener('paste', handlePaste);
    return () => el.removeEventListener('paste', handlePaste);
  }, [data, onDataChange]);

  const columnDefs = useMemo<ColDef[]>(() => {
    if (!data || !data.headers.length) return [];
    return data.headers.map((header, idx) => ({
      headerName: header,
      field: `col_${idx}`,
      editable: true,
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 80,
      cellStyle: () => ({
        fontSize: '12px',
      }),
    }));
  }, [data]);

  const rowData = useMemo(() => {
    if (!data || !data.rows.length) return [];
    return data.rows.map((row, rowIdx) => {
      const obj: Record<string, any> = { _rowId: rowIdx };
      data.headers.forEach((_, colIdx) => {
        obj[`col_${colIdx}`] = row[colIdx] ?? '';
      });
      return obj;
    });
  }, [data]);

  const onCellValueChanged = useCallback((event: any) => {
    if (!data) return;
    const colIdx = data.headers.indexOf(event.colDef.headerName);
    if (colIdx < 0) return;
    const newRows = [...data.rows];
    const rowIdx = event.rowIndex;
    if (newRows[rowIdx]) {
      newRows[rowIdx] = [...newRows[rowIdx]];
      const val = event.newValue;
      newRows[rowIdx][colIdx] = !isNaN(Number(val)) && val !== '' ? Number(val) : val;
      onDataChange({ ...data, rows: newRows });
    }
  }, [data, onDataChange]);

  const badge: DetectionBadge | null = data
    ? getDetectionBadge(data.detectedType, data.headers, data.columnTypes)
    : null;

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
  }), []);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        outline: 'none',
      }}
    >
      {/* Detection badge */}
      {badge && data && data.rows.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 12px',
          borderBottom: '1px solid var(--divider)',
          background: 'var(--hover-bg)',
        }}>
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 600,
            background: 'rgba(99, 102, 241, 0.15)',
            color: 'rgb(129, 120, 248)',
            textTransform: 'uppercase',
          }}>
            {badge.label}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            {badge.detail}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {data.rows.length} rows \u00D7 {data.headers.length} cols
          </span>
        </div>
      )}

      {/* Grid or placeholder */}
      <div style={{ flex: 1, minHeight: 0 }} className="ag-theme-alpine-dark">
        {!data || data.rows.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: '12px',
            color: 'var(--text-secondary)',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '48px', height: '48px', opacity: 0.3 }}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Paste data from Excel
              </div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>
                Ctrl+V (or Cmd+V) to paste tab/comma-separated data
              </div>
              <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '4px' }}>
                Or use "Load Example" from the toolbar
              </div>
            </div>
          </div>
        ) : (
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rowData}
            defaultColDef={defaultColDef}
            onCellValueChanged={onCellValueChanged}
            animateRows={false}
            getRowId={(params) => String(params.data._rowId)}
            suppressMovableColumns
            headerHeight={32}
            rowHeight={28}
          />
        )}
      </div>
    </div>
  );
};

export default SpectraDataGrid;
